import type { components, paths } from "./generated";

type Rel<Path> = Path extends `/api/v1${infer S}` ? S : never;
type Full<Path> = Path extends string ? `/api/v1${Path}` : never;

export type GetPaths = {
  [Path in keyof paths]: paths[Path] extends { get: unknown } ? Rel<Path> : never;
}[keyof paths];

export type PostPaths = {
  [Path in keyof paths]: paths[Path] extends { post: unknown } ? Rel<Path> : never;
}[keyof paths];

export type PutPaths = {
  [Path in keyof paths]: paths[Path] extends { put: unknown } ? Rel<Path> : never;
}[keyof paths];

export type PatchPaths = {
  [Path in keyof paths]: paths[Path] extends { patch: unknown } ? Rel<Path> : never;
}[keyof paths];

export type DeletePaths = {
  [Path in keyof paths]: paths[Path] extends { delete: unknown } ? Rel<Path> : never;
}[keyof paths];

export type RawGetResult<Path extends GetPaths> = paths[Full<Path>]["get"] extends {
  responses: infer Responses;
}
  ? JsonResponseByStatus<Responses, 200> | (204 extends keyof Responses ? null : never)
  : never;

// New command endpoints may acknowledge a write with 204 and no JSON body.
export type RawPostResult<Path extends PostPaths> = paths[Full<Path>]["post"] extends {
  responses: infer Responses;
}
  ? JsonResponseByStatus<Responses, 200> | (204 extends keyof Responses ? void : never)
  : never;

export type RawPutResult<Path extends PutPaths> =
  paths[Full<Path>]["put"]["responses"][200]["content"]["application/json"];

type JsonResponseByStatus<Responses, StatusCode extends string | number> =
  Responses extends Record<StatusCode, infer Response>
    ? Response extends { content: { "application/json": infer Body } }
      ? Body
      : never
    : never;

type OperationJsonResult<
  Path extends keyof paths,
  Method extends keyof paths[Path],
> = paths[Path][Method] extends { responses: infer Responses }
  ?
      | JsonResponseByStatus<Responses, 200>
      | JsonResponseByStatus<Responses, "200">
      | JsonResponseByStatus<Responses, 201>
      | JsonResponseByStatus<Responses, "201">
      | JsonResponseByStatus<Responses, 204>
      | JsonResponseByStatus<Responses, "204"> extends never
    ? any
    :
        | JsonResponseByStatus<Responses, 200>
        | JsonResponseByStatus<Responses, "200">
        | JsonResponseByStatus<Responses, 201>
        | JsonResponseByStatus<Responses, "201">
        | JsonResponseByStatus<Responses, 204>
        | JsonResponseByStatus<Responses, "204">
  : any;

type UnwrapResultOrSelf<Result> = Result extends { result?: infer Unwrapped }
  ? NonNullable<Unwrapped>
  : Result;

export type RawPatchResult<Path extends PatchPaths> = OperationJsonResult<Full<Path>, "patch">;

/**
 * Utility type to look up an API request model by its GET route
 */
export type GetRequest<Path extends GetPaths> = paths[Full<Path>]["get"] extends {
  parameters?: { query?: infer Query; path?: infer Args };
}
  ? Query & Args
  : undefined;

/**
 * Utility type to look up an API result model by its GET route
 */
export type GetResult<Path extends GetPaths> = UnwrapResultOrSelf<RawGetResult<Path>>;

/**
 * Utility type to look up an API request model by its POST route
 */
export type PostRequest<Path extends PostPaths> = paths[Full<Path>]["post"] extends {
  parameters?: { path?: infer Args };
  requestBody?: { content: { "application/json": infer Request } };
}
  ? Request extends unknown[] | string
    ? { $data: Request } & Args
    : Request & Args
  : undefined;

/**
 * Utility type to look up an API result model by its POST route
 */
export type PostResult<Path extends PostPaths> = UnwrapResultOrSelf<RawPostResult<Path>>;
/**
 * Utility type to look up an API request model by its PUT route
 */
export type PutRequest<Path extends PutPaths> = paths[Full<Path>]["put"] extends {
  parameters?: { path?: infer Args };
  requestBody?: { content: { "application/json": infer Request } };
}
  ? Request extends unknown[]
    ? { $data: Request } & Args
    : Request & Args
  : undefined;

/**
 * Utility type to look up an API result model by its PUT route, unwrapping the legacy
 * BaseCommandResult `{ result }` envelope when present (typed endpoints such as the
 * membership-freeze resume return their DTO directly).
 */
export type PutResult<Path extends PutPaths> = UnwrapResultOrSelf<RawPutResult<Path>>;

/**
 * Utility type to look up an API request model by its PATCH route
 */
export type PatchRequest<Path extends PatchPaths> = paths[Full<Path>]["patch"] extends {
  parameters?: { path?: infer Args };
  requestBody?: { content: { "application/json": infer Request } };
}
  ? Request extends unknown[]
    ? { $data: Request } & Args
    : Request & Args
  : undefined;

/**
 * Utility type to look up an API result model by its PATCH route
 */
export type PatchResult<Path extends PatchPaths> = UnwrapResultOrSelf<RawPatchResult<Path>>;

/**
 * Utility type to look up an API request model by its DELETE route
 */
export type DeleteRequest<Path extends DeletePaths> = paths[Full<Path>]["delete"] extends {
  parameters?: { path?: infer Args };
  requestBody?: { content: { "application/json": infer Request } };
}
  ? Request extends unknown[]
    ? { $data: Request } & Args
    : Request & Args
  : undefined;

/**
 * Utility type to look up an API model by its name
 */
export type Models<T extends keyof components["schemas"]> = components["schemas"][T];

export enum RewardStatus {
  NotAvailable,
  Available,
  Redeemed,
  CashedIn,
}

export enum ShopFeatureTarget {
  None,
  Treatment,
  Package,
  Membership,
  Category,
  CustomUri,
}

export enum DiscountType {
  Percentage,
  Cash,
}

export enum DiscountUse {
  Once,
  OncePerPerson,
  Unlimited,
}

export enum DiscountAppliesTo {
  AppliesToAll,
  AppliesToSpecific,
  AppliesToUnits,
}

export enum DiscountCombinator {
  All,
  Any,
  AnyWithPriceCap,
  AnyEqualOrLesser,
}

export enum DiscountCalculatorVersion {
  V1,
  V2,
}

export enum ShopDisplayType {
  Treatment,
  Package,
  Membership,
  Product,
  ProductFromExternalSource,
  GiftCard,
}

export enum FulfillmentMethod {
  Pickup,
  Shipping,
}

export enum PackageType {
  Service, // Treatment
  Product,
  ProductFromExternalSource,
  GiftCard,
}

export enum RetailProductListingStatus {
  OnMarketplace,
  NotVisibleInShop,
  OutOfStock,
}

export enum OrderDeliveryStatus {
  NotSet,
  Pending,
  Shipped,
  Delivered,
  Canceled,
}

export enum OrderPickupStatus {
  ReadyForPickup,
  PickedUp,
  Canceled,
}

export enum FulfillmentSource {
  SkinDrop,
  MyProducts,
}
